/**
 * Shadow-root plumbing.
 *
 * The widget renders on websites we do not control, built by people we will
 * never speak to, on themes that set `button { all: revert }` and worse. A
 * shadow root is the only reliable way to stop the host page's CSS reaching
 * our markup — and to stop ours leaking into theirs.
 */

export interface WidgetRoot {
  /** Element React renders into. Always carries the `ac-root` class. */
  container: HTMLElement
  /** The shadow root, or null when isolation was unavailable. */
  shadowRoot: ShadowRoot | null
  /** True when the widget is genuinely isolated from host page CSS. */
  isolated: boolean
  /** Adds another stylesheet to this root (used for lazily-loaded assets). */
  addStyles(css: string, key: string): void
  dispose(): void
}

type StyleTarget = ShadowRoot | HTMLElement

function supportsAdoptedStyleSheets(root: StyleTarget): root is ShadowRoot {
  return (
    typeof CSSStyleSheet !== 'undefined' &&
    typeof CSSStyleSheet.prototype.replaceSync === 'function' &&
    'adoptedStyleSheets' in root
  )
}

/**
 * Constructed stylesheets are shared between every widget instance on the
 * page — parsed once, adopted many times.
 */
const sharedSheets = new Map<string, CSSStyleSheet>()

function getSharedSheet(css: string, key: string): CSSStyleSheet | null {
  const cached = sharedSheets.get(key)
  if (cached) return cached

  try {
    const sheet = new CSSStyleSheet()
    sheet.replaceSync(css)
    sharedSheets.set(key, sheet)
    return sheet
  } catch {
    return null
  }
}

function applyStyles(root: StyleTarget, css: string, key: string): void {
  if (supportsAdoptedStyleSheets(root)) {
    const sheet = getSharedSheet(css, key)
    if (sheet) {
      if (!root.adoptedStyleSheets.includes(sheet)) {
        root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet]
      }
      return
    }
  }

  // Fallback: a <style> element inside the same root. Still scoped when the
  // root is a shadow root; global only in the no-shadow fallback below.
  const existing = root.querySelector(`style[data-ac-styles="${key}"]`)
  if (existing) return

  const style = document.createElement('style')
  style.setAttribute('data-ac-styles', key)
  style.textContent = css
  root.appendChild(style)
}

/**
 * Creates an isolated root inside `host`.
 *
 * Falls back to rendering directly into the host element when a shadow root
 * cannot be attached — some elements refuse one, and a widget that renders
 * unstyled is still better than a widget that throws.
 */
export function createWidgetRoot(host: HTMLElement, css: string): WidgetRoot {
  let shadowRoot: ShadowRoot | null = host.shadowRoot

  if (!shadowRoot) {
    try {
      shadowRoot = host.attachShadow({ mode: 'open' })
    } catch {
      shadowRoot = null
    }
  }

  const styleTarget: StyleTarget = shadowRoot ?? host
  applyStyles(styleTarget, css, 'core')

  const container = document.createElement('div')
  container.className = 'ac-root'
  styleTarget.appendChild(container)

  return {
    container,
    shadowRoot,
    isolated: shadowRoot !== null,
    addStyles(additionalCss: string, key: string) {
      applyStyles(styleTarget, additionalCss, key)
    },
    dispose() {
      container.remove()
    },
  }
}

/** Test seam: constructed stylesheets are cached for the page's lifetime. */
export function resetSharedSheetsForTests(): void {
  sharedSheets.clear()
}
